import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { clientsApi } from '@/services/api';
import { HiOutlineArrowLeft } from 'react-icons/hi';

const clientSchema = z.object({
  clientNumber: z.string().min(1, '利用者番号を入力してください'),
  lastName: z.string().min(1, '姓を入力してください'),
  firstName: z.string().min(1, '名を入力してください'),
  lastNameKana: z.string().optional(),
  firstNameKana: z.string().optional(),
  birthDate: z.string().min(1, '生年月日を入力してください'),
  gender: z.enum(['male', 'female', 'other']).optional(),
  postalCode: z.string().optional(),
  prefecture: z.string().optional(),
  city: z.string().optional(),
  address: z.string().optional(),
  phone: z.string().optional(),
  mobilePhone: z.string().optional(),
  email: z.string().email().optional().or(z.literal('')),
  disabilityType: z.string().optional(),
  disabilityGrade: z.string().optional(),
  supportCategory: z.number().min(1).max(6).optional(),
  recipientNumber: z.string().optional(),
  recipientValidFrom: z.string().optional(),
  recipientValidTo: z.string().optional(),
  upperLimitAmount: z.number().optional(),
  contractDate: z.string().optional(),
  emergencyContactName: z.string().optional(),
  emergencyContactRelationship: z.string().optional(),
  emergencyContactPhone: z.string().optional(),
  notes: z.string().optional(),
});

type ClientForm = z.infer<typeof clientSchema>;

export default function ClientCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ClientForm>({
    resolver: zodResolver(clientSchema),
  });

  const mutation = useMutation({
    mutationFn: (data: ClientForm) => clientsApi.create(data),
    onSuccess: () => {
      toast.success('利用者を登録しました');
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      navigate('/clients');
    },
    onError: (error: Error) => {
      toast.error(error.message || '登録に失敗しました');
    },
  });

  const onSubmit = (data: ClientForm) => {
    mutation.mutate(data);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/clients')} className="btn-ghost">
          <HiOutlineArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">利用者登録</h1>
          <p className="text-gray-600">新しい利用者を登録します</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* 基本情報 */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">基本情報</h2>
          </div>
          <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">利用者番号 *</label>
              <input {...register('clientNumber')} className={errors.clientNumber ? 'input-error' : 'input'} />
              {errors.clientNumber && <p className="text-sm text-red-600 mt-1">{errors.clientNumber.message}</p>}
            </div>
            <div className="md:col-span-2 grid grid-cols-2 gap-4">
              <div>
                <label className="label">姓 *</label>
                <input {...register('lastName')} className={errors.lastName ? 'input-error' : 'input'} />
                {errors.lastName && <p className="text-sm text-red-600 mt-1">{errors.lastName.message}</p>}
              </div>
              <div>
                <label className="label">名 *</label>
                <input {...register('firstName')} className={errors.firstName ? 'input-error' : 'input'} />
                {errors.firstName && <p className="text-sm text-red-600 mt-1">{errors.firstName.message}</p>}
              </div>
            </div>
            <div className="md:col-span-2 grid grid-cols-2 gap-4">
              <div>
                <label className="label">セイ</label>
                <input {...register('lastNameKana')} className="input" />
              </div>
              <div>
                <label className="label">メイ</label>
                <input {...register('firstNameKana')} className="input" />
              </div>
            </div>
            <div>
              <label className="label">生年月日 *</label>
              <input type="date" {...register('birthDate')} className={errors.birthDate ? 'input-error' : 'input'} />
              {errors.birthDate && <p className="text-sm text-red-600 mt-1">{errors.birthDate.message}</p>}
            </div>
            <div>
              <label className="label">性別</label>
              <select {...register('gender')} className="input">
                <option value="">選択してください</option>
                <option value="male">男性</option>
                <option value="female">女性</option>
                <option value="other">その他</option>
              </select>
            </div>
            <div>
              <label className="label">電話番号</label>
              <input {...register('phone')} className="input" />
            </div>
            <div>
              <label className="label">携帯電話</label>
              <input {...register('mobilePhone')} className="input" />
            </div>
            <div className="md:col-span-2">
              <label className="label">メールアドレス</label>
              <input type="email" {...register('email')} className="input" />
            </div>
          </div>
        </div>

        {/* 障害情報 */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">障害・受給者証情報</h2>
          </div>
          <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">障害種別</label>
              <select {...register('disabilityType')} className="input">
                <option value="">選択してください</option>
                <option value="physical">身体障害</option>
                <option value="intellectual">知的障害</option>
                <option value="mental">精神障害</option>
                <option value="developmental">発達障害</option>
                <option value="intractable">難病</option>
                <option value="multiple">重複障害</option>
              </select>
            </div>
            <div>
              <label className="label">障害支援区分</label>
              <select {...register('supportCategory', { valueAsNumber: true })} className="input">
                <option value="">選択してください</option>
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>区分{n}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">受給者番号</label>
              <input {...register('recipientNumber')} className="input" />
            </div>
            <div>
              <label className="label">負担上限月額</label>
              <input type="number" {...register('upperLimitAmount', { valueAsNumber: true })} className="input" />
            </div>
            <div>
              <label className="label">受給者証有効期間（開始）</label>
              <input type="date" {...register('recipientValidFrom')} className="input" />
            </div>
            <div>
              <label className="label">受給者証有効期間（終了）</label>
              <input type="date" {...register('recipientValidTo')} className="input" />
            </div>
          </div>
        </div>

        {/* 緊急連絡先 */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">緊急連絡先</h2>
          </div>
          <div className="card-body grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label">氏名</label>
              <input {...register('emergencyContactName')} className="input" />
            </div>
            <div>
              <label className="label">続柄</label>
              <input {...register('emergencyContactRelationship')} className="input" />
            </div>
            <div>
              <label className="label">電話番号</label>
              <input {...register('emergencyContactPhone')} className="input" />
            </div>
          </div>
        </div>

        {/* 備考 */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">備考</h2>
          </div>
          <div className="card-body">
            <textarea {...register('notes')} rows={4} className="input" />
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button type="button" onClick={() => navigate('/clients')} className="btn-secondary">
            キャンセル
          </button>
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? '登録中...' : '登録する'}
          </button>
        </div>
      </form>
    </div>
  );
}


